import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { Navigate, useNavigate } from '@tanstack/react-router';

import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { Can, useAbilityContext } from '#state/contexts/ability-context.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { DemoProjectsCloneControl } from './demo-projects-clone-control.js';
import { DemoProvisioningState } from './empty-state/demo-provisioning-state.js';
import { DemoUnavailableNotice } from './empty-state/demo-unavailable-notice.js';
import { EmptyStateChoices } from './empty-state/empty-state-choices.js';
import { EmptyStateHero } from './empty-state/empty-state-hero.js';
import { useLazyDemoSeed } from './empty-state/use-lazy-demo-seed.js';

import type { HilProjectPreview } from '@cadstrom/backend/shared';
import type { SampleDoor } from './empty-state/empty-state-choices.js';

export interface ProjectsEmptyStateProps {
	orgName: string;
	/** A demo project already in the org, if any; the sample door opens it directly. */
	demoProject?: HilProjectPreview | null;
}

/**
 * First-run empty state for the Projects list: a circuit hero and header above either a two-path
 * chooser (explore a sample project vs. create your own — shown when the `demo-project` feature flag
 * is on) or a direct route into creating one. The sample door opens an existing demo when one is
 * already in the org, otherwise it seeds one on demand, showing an anticipation state while it
 * provisions and degrading to a non-blocking notice if it can't.
 *
 * Choosing "create" opens the upload flow, which names the project from the files.
 */
export function ProjectsEmptyState({ orgName, demoProject = null }: ProjectsEmptyStateProps) {
	const navigate = useNavigate();
	const el = useDataElTagGenerator(ProjectsEmptyState);

	const ability = useAbilityContext();
	const isDemoProjectFlagEnabled = useFeatureFlag('demo-project');
	const canManageProjects = ability.can('manage', 'project');
	// Seeding a demo hits a manage-org backend operation, so only offer the seed door to
	// users who can manage the org; opening an already-published demo needs no such right.
	const canSeedDemo = ability.can('manage', 'organization');

	const lazySeed = useLazyDemoSeed(orgName, canSeedDemo);

	const demoRevisionId = demoProject?.latestRevision?.id;
	// `undefined` means PostHog hasn't resolved flags yet; fall back to the direct create action
	// rather than flashing the chooser in and out while flags load. The sample door shows even
	// without a demo already in the list — it seeds one on demand (lazy per-org seed).
	const showChoices = isDemoProjectFlagEnabled === true;

	// An existing demo links straight in; otherwise the door seeds one on click, but only
	// for users who can manage the org (the seed backend requires it). `null` hides it.
	const sampleDoor: SampleDoor | null =
		demoProject !== null && demoRevisionId !== undefined ?
			{ mode: 'open', demoProjectId: demoProject.id, demoRevisionId }
		: canSeedDemo ? { mode: 'seed', onExplore: lazySeed.startSeed, loading: lazySeed.status === 'seeding' }
		: null;

	const startUploadFirst = () => {
		void navigate({ to: '/orgs/$orgName/projects/new/upload', params: { orgName } });
	};

	return (
		<div {...el()} className='flex grow flex-col items-center justify-center overflow-auto'>
			<div {...el('column')} className='flex w-full max-w-3xl -translate-y-16 flex-col items-center gap-8 px-3'>
				<div className='w-full max-w-2xl'>
					<EmptyStateHero />
				</div>
				<div className='flex max-w-2xl flex-col items-center gap-3.5 text-center'>
					<h1 className='text-7xl font-bold'>How do you want to start?</h1>
				</div>

				{showChoices ?
					lazySeed.status === 'ready' && lazySeed.clonedProjectId !== null && lazySeed.clonedRevisionId !== null ?
						<Navigate
							to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/results'
							params={{
								orgName,
								projectId: lazySeed.clonedProjectId,
								revisionId: lazySeed.clonedRevisionId,
							}}
						/>
					: lazySeed.status === 'seeding' ?
						<DemoProvisioningState />
					:	<div className='flex w-full flex-col items-center gap-4'>
							{lazySeed.status === 'unavailable' && <DemoUnavailableNotice onRetry={lazySeed.startSeed} />}
							<EmptyStateChoices
								sample={sampleDoor}
								canCreateProject={canManageProjects}
								onCreateProject={startUploadFirst}
							/>
						</div>

				:	<Can I='manage' a='project'>
						<Button variant='contained' size='large' startIcon={<AddIcon />} onClick={startUploadFirst}>
							<span>Create project</span>
						</Button>
					</Can>
				}

				{demoProject === null && <DemoProjectsCloneControl orgName={orgName} projects={[]} className='self-stretch' />}
			</div>
		</div>
	);
}
